// web/src/scene/SceneRefContext.ts
import { createContext, useContext } from 'react'
import type { MutableRefObject } from 'react'
import type * as THREE from 'three'

export interface SceneRef {
  camera: {
    /** Switch OrbitControls between rotate-only and pan-only modes */
    setMode: (mode: 'orbit' | 'pan') => void
    /** Move camera toward the orbit target. factor < 1 = closer. Default 0.8. */
    zoomIn: (factor?: number) => void
    /** Move camera away from the orbit target. factor > 1 = farther. Default 1.25. */
    zoomOut: (factor?: number) => void
    /** Restore camera to the last saved position (bootstrap or fitToModel). */
    reset: () => void
    /**
     * Frame a bounding box in view. Passing `box` seeds the internal cache so
     * later no-arg calls (e.g., the toolbar button) reuse it. Without a box,
     * the cached box is used — and if none has been seeded yet, the call is
     * a no-op.
     */
    fitToModel: (box?: THREE.Box3) => void
    /**
     * Smooth fly-to toward a world-space position.
     *
     * @param position      world-space target
     * @param viewDistance  orbit radius after fly-to (default 150)
     * @param duration      animation length in **SECONDS** (default 1.2).
     *                      NOT milliseconds — the useFrame loop divides
     *                      per-frame delta (seconds) by this value.
     * @param approachDir   OPTIONAL unit vector describing which side of
     *                      the target the camera should end up on (with
     *                      some lift). Default `(0, 0.85, 0.5)` frames
     *                      the front of equipment GLBs. Personnel GLBs
     *                      have an opposite forward axis — pass a negated
     *                      vector for them.
     */
    flyTo: (
      position: { x: number; y: number; z: number },
      viewDistance?: number,
      duration?: number,
      approachDir?: { x: number; y: number; z: number },
    ) => void
  }
}

export const SceneRefContext =
  createContext<MutableRefObject<SceneRef | null> | null>(null)

export function useSceneRef(): MutableRefObject<SceneRef | null> {
  const ctx = useContext(SceneRefContext)
  if (!ctx) {
    throw new Error('useSceneRef must be used within <SceneRefContext.Provider>')
  }
  return ctx
}
